import {isEmpty, isFunction, find, some, filter, map, get, extend, isEqual} from 'lodash';
import React, {useRef, useCallback, useMemo, useContext, useState, useEffect} from 'react';
import classnames from 'classnames';
import {Table, Button, Link, DialogBox, Modal, Drawer, Loading, Pagination} from 'acud';
import {TableProps} from 'acud/lib/table';

import {ConfigContext} from '../../common/ConfigProvider';
import {ACUD_PRO_PREFIX} from '../../_common/constants';

import EmptyStatus, {EmptyStatusProps} from '../EmptyStatus';
import Toolbar, {ToolbarProps} from './Toolbar';

import {useLocale} from '../../../components/locale';

import './index.less';

export interface CustomRenderProps {
    title?: React.ReactNode;
    toolbar?: React.ReactNode;
}

export interface SetColumnsProps {
    disabledColumns?: string[];
}

export interface HooksTableProps<RecordType> {
    className?: string;
    prefixCls?: string;
    title?: React.ReactNode;
    withToolbar?: boolean;
    customRender?: CustomRenderProps;
    toolbarProps?: ToolbarProps;
    tableProps?: TableProps<RecordType>;
    tableComp?: React.ComponentType<any>
    card?: React.ComponentType<any>
    emptyConfig?: {
        default?: EmptyStatusProps,
        create?: EmptyStatusProps,
        search?: EmptyStatusProps
    };
    dialogProps?: any;
    tableActions?: any;
    filterData?: any;
    listParams?: any;
    httpStatusCode?: number;
    setListParams?: any;
    handleCommand?: any;
    handleTableCommand?: any;
    handleFilterChange?: any;
    setColumnsConfig?: SetColumnsProps;
}

const EMPTY_STATUS_MAP = {
    '403': 'NO_AUTH',
    '404': 'NO_PAGE',
    '500': 'SERVER_ERROR'
};

const isReactNode = (value: any): value is React.ReactNode => {
    return (
        React.isValidElement(value)
        || typeof value === 'string'
        || typeof value === 'number'
        || typeof value === 'boolean'
        || value === undefined
        || value === null
    );
};

const Card = (props: any) => {
    const {
        prefixCls,
        loading,
        rowKey,
        dataSource,
        rowSelection,
        locale = {},
        card: CardContent,
        pagination,
        handleCommand,
        handleTableCommand
    } = props;

    const selectedRowKeys = rowSelection?.selectedRowKeys;

    const {className, ...paginationProps} = pagination || {};

    const paginationCls = classnames(`${prefixCls}-table-pagination ${prefixCls}-table-pagination-right`, className);

    const handleCheckboxChange = useCallback((e: any, id: any) => {
        if (!rowSelection) {
            return;
        }
        const checked = e.target.checked;
        const {selectedRowKeys, onChange} = rowSelection || {};
        const newSelectedRowKeys = checked ? [...selectedRowKeys, id] : filter(selectedRowKeys, key => key !== id);
        onChange?.(newSelectedRowKeys);
    }, [rowSelection]);

    return (
        <Loading loading={loading}>
            {!loading && dataSource?.length ? (
                <>
                    {CardContent && map(dataSource, (item: any, itemIndex) => (
                        <CardContent
                            key={item[rowKey ?? itemIndex]}
                            item={item}
                            selectedRowKeys={selectedRowKeys}
                            handleCommand={handleCommand}
                            handleTableCommand={handleTableCommand}
                            onCheckboxChange={handleCheckboxChange}
                        />
                    ))}
                    {pagination && pagination?.total && (
                        <Pagination
                            className={paginationCls}
                            {...paginationProps}
                        />
                    )}
                </>
            ) : locale.emptyText}
        </Loading>
    );
};

const Dialog = (props: any) => {
    const [locale] = useLocale('HooksTable');
    const {
        className,
        tablePrefixCls,
        dialogType,
        dialogSubType,
        content: Content,
        payload,
        foot,
        okText = locale?.sure,
        cancelText = locale?.cancel,
        cancelWithSuccess,
        confirmLoading,
        confirmLoadingText = locale?.submitting,
        onCancel,
        onClose,
        onOk,
        onSuccess,
        onConfirmLoading,
        onCancelConfirmLoading,
        handleDialogClose,
        ...compProps
    } = props;

    const contentRef = useRef();

    // modal是onCancel，drawer是onClose，这里不区分，onCancel和onClose都支持
    const onDialogCancel = onCancel || onClose;

    const handleDialogCancel = useCallback(() => {
        // TODO: 可能会加一个beforeCancel方法的支持
        handleDialogClose();
        isFunction(onDialogCancel) && onDialogCancel();
        cancelWithSuccess && onSuccess?.();
    }, [cancelWithSuccess, onDialogCancel, onSuccess, handleDialogClose]);

    const handleDialogOk = useCallback(() => {
        if (isFunction(onOk)) {
            onOk();
            return;
        }
        onConfirmLoading?.(confirmLoadingText);
        const {doSubmit} = contentRef.current || {} as any;
        isFunction(doSubmit) ? doSubmit().finally(onCancelConfirmLoading) : handleDialogClose();
    }, [confirmLoadingText, onOk, onConfirmLoading, onCancelConfirmLoading, handleDialogClose]);

    const handleDialogSuccess = useCallback((result?: any) => {
        // 父组件传过来的成功后执行的方法，一般是列表刷新
        onSuccess?.(result);
        // 关闭弹窗
        handleDialogClose();
    }, [onSuccess, handleDialogClose]);

    // Modal的自带footer有缺陷，这里重新定义默认footer
    const footer = useMemo(() => {
        if (foot === false) {
            return null;
        }
        if (dialogSubType === 'plain') {
            return (
                <Button
                    type="primary"
                    className={`${tablePrefixCls}-${dialogType}-ok-btn`}
                    onClick={handleDialogCancel}
                >
                    {okText}
                </Button>
            );
        }
        return (
            <>
                <Button
                    type="default"
                    className={`${tablePrefixCls}-${dialogType}-cancel-btn`}
                    onClick={handleDialogCancel}
                >
                    {cancelText}
                </Button>
                <Button
                    type="primary"
                    className={`${tablePrefixCls}-${dialogType}-ok-btn`}
                    disabled={confirmLoading}
                    loading={confirmLoading}
                    onClick={handleDialogOk}
                >
                    {confirmLoading ? confirmLoadingText : okText}
                </Button>
            </>
        );
    }, [
        foot,
        dialogType,
        dialogSubType,
        tablePrefixCls,
        cancelText,
        okText,
        confirmLoading,
        confirmLoadingText,
        handleDialogOk,
        handleDialogCancel
    ]);

    const contentComp = useMemo(() => {
        if (isReactNode(Content)) {
            return Content;
        }
        // 兼容Content组件被withRouter包裹后ref无法透传，需要用wrappedComponentRef替代的场景
        // https://v5.reactrouter.com/web/api/withRouter/wrappedcomponentref-func
        const wrappedComponentRef = Content?.WrappedComponent ? {wrappedComponentRef: contentRef} : {};
        return (
            <Content
                ref={contentRef}
                {...wrappedComponentRef}
                payload={payload}
                onCancel={handleDialogCancel}
                onSuccess={handleDialogSuccess}
                onConfirmLoading={onConfirmLoading}
                onCancelConfirmLoading={onCancelConfirmLoading}
            />
        );
    }, [Content, payload, onConfirmLoading, onCancelConfirmLoading, handleDialogCancel, handleDialogSuccess]);

    const dialogCls = classnames(`${tablePrefixCls}-${dialogType}`, className);

    if (dialogType === 'dialog-box') {
        return (
            <DialogBox
                visible
                className={dialogCls}
                content={contentComp}
                footer={footer}
                destroyOnClose
                onCancel={handleDialogCancel}
                {...compProps}
            />
        );
    }

    if (dialogType === 'modal') {
        return (
            <Modal
                visible
                className={dialogCls}
                footer={footer}
                destroyOnClose
                onCancel={handleDialogCancel}
                {...compProps}
            >
                {contentComp}
            </Modal>
        );
    }

    if (dialogType === 'drawer') {
        return (
            <Drawer
                visible
                className={dialogCls}
                footerStyle={{textAlign: 'right'}}
                footer={footer}
                destroyOnClose
                onClose={handleDialogCancel}
                {...compProps}
            >
                {contentComp}
            </Drawer>
        );
    }

    return null;
};

const HooksTable = <RecordType extends object = any>(props: HooksTableProps<RecordType>) => {
    const {
        className,
        prefixCls: customizePrefixCls,
        title,
        customRender = {},
        toolbarProps,
        tableProps,
        tableComp: TableComp,
        card,
        emptyConfig,
        filterData,
        dialogProps,
        httpStatusCode,
        handleCommand,
        handleTableCommand,
        handleFilterChange,
        setColumnsConfig
    } = props;
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [locale] = useLocale('HooksTable');

    const {prefixCls: defaultPrefixCls} = useContext(ConfigContext);
    const prefixCls = customizePrefixCls || defaultPrefixCls || ACUD_PRO_PREFIX;
    const {
        default: emptyDefaultConfig,
        create: emptyCreateConfig,
        search: emptySearchConfig
    } = emptyConfig || {};

    const tablePrefixCls = `${prefixCls}-hooks-table`;
    const tableCls = classnames(tablePrefixCls, className);

    const {withToolbar, bulkProps: {bulkComp, bulkConfig} = {}} = toolbarProps as ToolbarProps;

    const [currentColumns, setCurrentColumns] = useState(toolbarProps?.columns);

    const isColumnsChange = () => {
        return !isEqual(currentColumns, toolbarProps?.columns);
    };

    useEffect(() => {
        if (isColumnsChange() && toolbarProps?.columns) {
            setColumns(toolbarProps.columns);
        }
    }, [toolbarProps?.columns]);

    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null;
        if (!toolbarProps?.controlsConfig?.colsNum) {
            if (!containerRef.current) {
                return;
            };

            resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const {width} = entry.contentRect;
                    setContainerWidth(width);
                }
            });

            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver?.disconnect();
        };
    }, [toolbarProps]);

    const setColumns = useCallback((columns: any[]) => {
        const filteredColumns = columns.filter((column: any) => !column.hidden);
        // const filteredColumns = columns.filter((column: any) => column.show);
        setCurrentColumns(filteredColumns);
    }, []);

    const withFilterData = useMemo(() => {
        if (isEmpty(filterData)) {
            return false;
        }
        return some(filterData, filterDataItem => !isEmpty(filterDataItem));
    }, [filterData]);

    const emptyProps = useMemo(() => {
        if (httpStatusCode) {
            return {
                status: EMPTY_STATUS_MAP[String(httpStatusCode)] || 'NO_DATA'
            };
        }

        let statusEmptyProps = extend({status: 'NO_DATA', children: <></>}, emptyDefaultConfig) as EmptyStatusProps;
        if (withFilterData) {
            extend(statusEmptyProps, emptySearchConfig);
        }
        else if (withToolbar) {
            if (emptyCreateConfig) {
                extend(statusEmptyProps, emptyCreateConfig);
            }
            else if (!bulkComp && bulkConfig) {
                const createConfig = find(bulkConfig, ({id}) =>
                    /^create/.test(id)
                );
                const {text, disabled, ...restCreateConfig}
                    = createConfig || {};
                // const reg = new RegExp(/创建|新建/)
                const reg = new RegExp(locale?.create + '|' + locale?.new);
                statusEmptyProps.description = text
                    ? locale?.no
                      + text.replace(reg, ($1: any) => `${$1}${locale?.any}`)
                    : locale?.noData;
                statusEmptyProps.children = text && (
                    <Link
                        disabled={disabled}
                        onClick={() =>
                            handleCommand?.({config: restCreateConfig})
                        }
                    >
                        {text}
                    </Link>
                );
            }
        }
        return statusEmptyProps;
    }, [
        httpStatusCode,
        withFilterData,
        withToolbar,
        bulkComp,
        bulkConfig,
        handleCommand,
        locale,
        emptyDefaultConfig,
        emptyCreateConfig,
        emptySearchConfig
    ]);

    const tableContent = useMemo(() => {
        let content = null as React.ReactNode;
        if (TableComp) {
            content = (
                <TableComp
                    {...tableProps}
                    columns={currentColumns}
                    handleCommand={handleCommand}
                    handleTableCommand={handleTableCommand}
                />
            );
        }
        else if (card) {
            content = (
                <Card
                    prefixCls={prefixCls}
                    locale={{
                        emptyText: <EmptyStatus {...emptyProps} />
                    }}
                    {...tableProps}
                    columns={currentColumns}
                    card={card}
                    handleCommand={handleCommand}
                    handleTableCommand={handleTableCommand}
                />
            );
        }
        else {
            content = (
                <Table
                    locale={{
                        emptyText: <EmptyStatus {...emptyProps} />
                    }}
                    {...tableProps}
                    columns={currentColumns}
                />
            );
        }
        return content;
    }, [prefixCls, TableComp, card, emptyProps, handleCommand, handleTableCommand, tableProps, currentColumns]);

    const selectedProps = useMemo(() => {
        const total = get(tableProps, 'pagination.total');
        const selectedRowKeys = get(tableProps, 'rowSelection.selectedRowKeys');
        return {
            selectedRowKeys,
            total
        };
    }, [tableProps]);

    return (
        <div className={tableCls} ref={containerRef}>
            {customRender.title}
            {title && <h2>{title}</h2>}
            <Toolbar
                prefixCls={prefixCls}
                {...toolbarProps}
                selectedProps={selectedProps}
                handleCommand={handleCommand}
                setColumnsConfig={setColumnsConfig}
                setColumns={setColumns}
                handleFilterChange={handleFilterChange}
                containerWidth={containerWidth}
            />
            {customRender.toolbar}
            {tableContent}
            <Dialog tablePrefixCls={tablePrefixCls} {...dialogProps} />
        </div>
    );
};


export default HooksTable;
